import { createContext, use, useCallback, useMemo, useState, type ReactNode } from 'react';
import { guardarIdioma, idiomaInicial } from './idioma';
import { crearTraductor, type Idioma, type Traductor } from './traducir';

interface Contexto {
  idioma: Idioma;
  t: Traductor;
  cambiarIdioma: (idioma: Idioma) => void;
}

const ContextoIdioma = createContext<Contexto | null>(null);

/**
 * Envuelve la aplicación y le da el idioma a todo lo de adentro.
 *
 * Va en el `main.tsx`, por fuera del router: cambiar de idioma no debería
 * cambiar de ruta ni al revés.
 */
export function ProveedorIdioma({ children }: { children: ReactNode }) {
  const [idioma, setIdioma] = useState<Idioma>(idiomaInicial);

  const cambiarIdioma = useCallback((nuevo: Idioma) => {
    setIdioma(nuevo);
    guardarIdioma(nuevo);
    // El atributo `lang` del documento no es decorativo: de él dependen el
    // lector de pantalla para pronunciar bien, el corrector del navegador y la
    // separación de palabras al final de línea.
    document.documentElement.lang = nuevo;
  }, []);

  // El traductor se rehace solo cuando cambia el idioma, no en cada dibujado.
  const valor = useMemo<Contexto>(
    () => ({ idioma, t: crearTraductor(idioma), cambiarIdioma }),
    [idioma, cambiarIdioma],
  );

  return <ContextoIdioma value={valor}>{children}</ContextoIdioma>;
}

/**
 * El idioma actual, la función para traducir y la forma de cambiarlo.
 *
 *   const { t } = useTraduccion();
 *   return <h1>{t('admin.inicio.titulo')}</h1>;
 *
 * Falla en voz alta si se usa fuera del proveedor. Sin eso, un componente mal
 * ubicado devolvería textos vacíos y habría que adivinar por qué.
 */
export function useTraduccion(): Contexto {
  const contexto = use(ContextoIdioma);
  if (!contexto) {
    throw new Error('useTraduccion() necesita estar dentro de <ProveedorIdioma>.');
  }
  return contexto;
}
